import { useEffect, useState } from 'react';

import { Logo } from './assets/Logo.js';

type HealthState = 'checking' | 'ok' | 'unreachable';

const MESSAGE: Record<HealthState, string> = {
  checking: 'Checking the server…',
  ok: 'Server connected',
  unreachable: 'Server not reachable yet',
};

const DOT_COLOUR: Record<HealthState, string> = {
  checking: 'var(--color-text-placeholder)',
  ok: 'var(--color-success)',
  unreachable: 'var(--color-warning)',
};

/** Asks the server once whether it is alive. */
function useHealth(): HealthState {
  const [health, setHealth] = useState<HealthState>('checking');

  useEffect(() => {
    let cancelled = false;
    fetch('/api/health')
      .then((response) => {
        if (!cancelled) setHealth(response.ok ? 'ok' : 'unreachable');
      })
      .catch(() => {
        if (!cancelled) setHealth('unreachable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return health;
}

function StatusPill({ health }: { health: HealthState }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-4)',
        borderRadius: 'var(--radius-full)',
        background: 'var(--color-surface-sunken)',
        color: 'var(--color-text-secondary)',
        fontSize: 'var(--text-sm)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 'var(--radius-full)',
          background: DOT_COLOUR[health],
        }}
      />
      {MESSAGE[health]}
    </div>
  );
}

/**
 * Phase 0 placeholder.
 *
 * Its only job is to prove the whole pipeline works end to end: the page builds, the
 * brand renders, and the browser can reach the server. Phase 1 replaces this with the
 * real landing page (docs/06-UI-SPEC.md §2).
 */
export function App() {
  const health = useHealth();

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--color-surface-sunken)',
        padding: 'var(--space-6)',
      }}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          padding: 'var(--space-12) var(--space-16)',
          textAlign: 'center',
          maxWidth: 560,
        }}
      >
        <Logo size={56} />

        <p
          style={{
            marginTop: 'var(--space-6)',
            marginBottom: 'var(--space-8)',
            color: 'var(--color-text-secondary)',
            fontSize: 'var(--text-base)',
            lineHeight: 'var(--text-base-lh)',
          }}
        >
          A real-time collaborative maths whiteboard for online tutoring.
        </p>

        <StatusPill health={health} />
      </div>
    </main>
  );
}
